/**
 * Date utilities for consistent timezone handling.
 *
 * All analytics data is stored in UTC on the backend.
 * These utilities help ensure consistent interpretation and display.
 */

/**
 * Format a Date to YYYY-MM-DD string using local date components.
 * Use this when sending date ranges to APIs that expect local-context dates.
 */
export const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a UTC timestamp for display in user's local timezone.
 * Use this for displaying entry/exit times, ALPR timestamps, etc.
 */
export const formatUTCToLocal = (
  isoString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString(undefined, options);
};

/**
 * Format a date-only string (YYYY-MM-DD) for display, treating it as a UTC date.
 * Use this for analytics data where dates represent UTC calendar days.
 */
export const formatDateAsUTC = (
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateStr) return 'N/A';
  // Append T00:00:00Z to interpret as UTC midnight
  const date = new Date(dateStr + 'T00:00:00Z');
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    ...options
  });
};

/**
 * Parse a date-only string as UTC midnight Date object.
 * Use this when you need a Date object from analytics date strings.
 */
export const parseDateAsUTC = (dateStr: string): Date => {
  return new Date(dateStr + 'T00:00:00Z');
};

/**
 * Check if two dates represent the same UTC calendar day.
 */
export const isSameUTCDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

/**
 * Get today's date as a UTC midnight Date object.
 */
export const getTodayUTC = (): Date => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};
